import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layers, Loader2, Network, RefreshCw } from 'lucide-react';
import { useLcs } from '@/hooks/useLcs';
import { persistenceMode } from '@/lib/repo';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const TOPIC_CHIP =
  'inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] ' +
  'text-muted-foreground hover:text-foreground';

// The EVENT-BANK tier (SENTENCE → EVENT → EVENT-BANK → ARCHIVE): a cross-topic
// index where the same real-world event covered under different topics collapses
// into one reusable bank entry.
export default function EventBank() {
  const { eventBank, error, loadEventBank, rebuildEventBank } = useLcs();
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void loadEventBank().finally(() => setLoaded(true));
  }, [loadEventBank]);

  const canRebuild = persistenceMode === 'cloud';
  const crossTopic = eventBank.filter((e) => e.cross_topic).length;

  async function rebuild() {
    setBusy(true);
    try {
      await rebuildEventBank();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container mx-auto max-w-7xl px-6 py-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Event Bank</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            A cross-topic index of every event. The same real-world event covered under different topics is collapsed into one
            reusable bank entry — so the Story Engine sees a single, deduplicated event across the whole archive.
          </p>
        </div>
        {canRebuild ? (
          <Button size="sm" onClick={rebuild} disabled={busy}>
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}
            {busy ? 'Rebuilding…' : eventBank.length ? 'Rebuild bank' : 'Build bank'}
          </Button>
        ) : (
          <span className="text-[11px] text-muted-foreground">The Event Bank needs the deployed backend (cloud mode).</span>
        )}
      </div>

      {error && <div className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-600">{error}</div>}

      {!loaded ? (
        <div className="py-16 text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : eventBank.length === 0 ? (
        <div className="mt-6 rounded-lg border bg-card py-12 text-center">
          <Network className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            The bank is empty. Cluster atoms into events inside your topics first, then build the bank to link the same events
            across topics.
          </p>
        </div>
      ) : (
        <>
          <div className="my-5 flex flex-wrap items-center gap-2">
            <Badge variant="secondary">{eventBank.length} bank entries</Badge>
            <Badge variant="info">{crossTopic} span multiple topics</Badge>
          </div>
          <div className="grid gap-3 lg:grid-cols-2">
            {eventBank.map((e) => (
              <div key={e.bank_id} className="rounded-lg border bg-card p-4 shadow-sm">
                <div className="mb-1 flex items-center gap-2">
                  <Badge variant={e.cross_topic ? 'success' : 'info'}>
                    <Network className="mr-1 h-3 w-3" />
                    {e.cross_topic ? 'Cross-topic' : 'Event'}
                  </Badge>
                  <span className="text-sm font-medium">{e.label}</span>
                  <span className="ml-auto text-[11px] text-muted-foreground">{e.event_refs.length} events</span>
                </div>
                {(e.actor || e.action || e.object || e.location) && (
                  <p className="mb-2 text-xs text-muted-foreground">
                    {[e.actor, e.action, e.object, e.location].filter(Boolean).join(' · ')}
                  </p>
                )}
                <div className="mt-2 border-t border-border pt-2">
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Appears in {e.topic_ids.length} topic{e.topic_ids.length === 1 ? '' : 's'}
                  </p>
                  <ul className="space-y-1">
                    {e.event_refs.map((r) => (
                      <li key={r.event_id} className="flex items-center gap-2 text-[12px]">
                        <Link to={`/topics/${r.topic_id}`} className={TOPIC_CHIP}>
                          <Layers className="mr-1 h-3 w-3" />
                          {r.topic_title}
                        </Link>
                        <span className="truncate text-foreground">{r.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
